// SmootherOPE から移した管理ページ（立替払い・入居申込・書類確認）で共通に使う表示・変換の部品。
// Step 3 の本審査ページ（features/main-examination/components/shared.tsx）と同じ振る舞いにそろえている
import axios from "axios"
import dayjs from "dayjs"
import React from "react"
import { Loader, Notice } from "@smooth/design-system-components"

export type ScreenMessage = { tone: "info" | "error"; text: string }

export const toNumberOrNull = (v: unknown): number | null => {
  if (v == null || v === "") return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export const toStringOrNull = (v: string | null | undefined): string | null =>
  v == null || v === "" ? null : v

// 真偽値の選択肢。未設定は空文字で表し、送信時に null に戻す
export const toBooleanOrNull = (v: unknown): boolean | null =>
  v === "true" || v === true ? true : v === "false" || v === false ? false : null

export const toSelectValue = (v: boolean | null | undefined) => (v == null ? "" : String(v))

export const formatDateTime = (v: string | null | undefined) =>
  v ? dayjs(v).format("YYYY/MM/DD HH:mm:ss") : "—"

export const formatDate = (v: string | null | undefined) =>
  v ? dayjs(v).format("YYYY/MM/DD") : "—"

// datetime-local の入力欄に入れる値
export const toDateTimeInputValue = (v: string | null | undefined) =>
  v ? dayjs(v).format("YYYY-MM-DDTHH:mm:ss") : ""

export const formatNumber = (v: number | string | null | undefined, unit = "") =>
  v != null && v !== "" ? `${Number(v).toLocaleString()}${unit}` : "—"

export const formatText = (v: string | number | null | undefined) =>
  v != null && v !== "" ? String(v) : "—"

const isNotFound = (error: Error | null) =>
  axios.isAxiosError(error) && error.response?.status === 404

// API のエラー（{ message, errors? }）を画面に出す文言にする。
// errors は項目ごとのメッセージ（{ 項目: [文言] }）を想定し、どの項目が不正かを並べる
export const describeApiError = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) return fallback
  const data = error.response?.data as
    | { message?: unknown; errors?: Record<string, unknown> | unknown[] }
    | undefined
  const message = typeof data?.message === "string" && data.message ? data.message : null
  const details: string[] = []
  if (Array.isArray(data?.errors)) {
    data.errors.forEach((e) => details.push(String(e)))
  } else if (data?.errors && typeof data.errors === "object") {
    Object.entries(data.errors).forEach(([key, value]) => {
      const texts = Array.isArray(value) ? value.map(String) : [String(value)]
      texts.forEach((t) => details.push(`${key}: ${t}`))
    })
  }
  const head = message ? `${fallback}（${message}）` : fallback
  return details.length > 0 ? `${head}\n${details.join("\n")}` : head
}

// 読み込み中・失敗・見つからないときの表示。表示するものがあれば null を返す
export const LoadState: React.FC<{
  isLoading: boolean
  error: Error | null
  found: boolean
  label: string
}> = ({ isLoading, error, found, label }) => {
  if (isLoading) return <Loader />
  if (isNotFound(error) || (!error && !found)) {
    return <Notice variant="card">{label}が見つかりません</Notice>
  }
  if (error) {
    return (
      <Notice tone="error" variant="card">
        データの読み込みに失敗しました
      </Notice>
    )
  }
  return null
}

export const MessageNotice: React.FC<{ message: ScreenMessage | null }> = ({ message }) =>
  message ? (
    <Notice tone={message.tone} variant="card">
      <span className="whitespace-pre-wrap">{message.text}</span>
    </Notice>
  ) : null

// SWR で 1 件を取る hook の共通部分
export const SWR_DETAIL_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  errorRetryCount: 3,
} as const
